import { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import { Amplify, API, graphqlOperation, Auth } from "aws-amplify";
import "./styles.css";
import "@aws-amplify/ui-react/styles.css";
import awsExports from "./aws-exports";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "./layout/Layout";
import { routes } from "./routes";
import { Onboarding } from "./pages/auth/Onboarding";
import Login from "./pages/auth/Login";
import { PaymentSuccess } from "./pages/auth/PaymentSuccess";
import { PaymentFailed } from "./pages/auth/PaymentFailed";
import ForgotPassword from "./pages/auth/ForgotPassword";
import { TrialExpirationChecker } from "./components/TrialExpirationChecker";
import GoogleAnalytics from "./Analytics/GoogleAnalytics";
import { sendGAEvent } from "./utils/analytics";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { ViewLimitProvider } from "./context/ViewLimitContext";

Amplify.configure({
    ...awsExports,
    Auth: {
        region: "eu-central-1",
        userPoolId: "eu-central-1_1IbkiA4fq",
        userPoolWebClientId: "5t2a9r0bm75016q1joepsaslab",
        identityPoolId: "eu-central-1:68883f57-6aec-4102-a886-0394d3f3e7d2",
        identityPoolRegion: "eu-central-1"
    },
    Storage: {
        AWSS3: {
            bucket: 'competitor-data-tendertracernl',
            region: 'eu-west-3',
            customPrefix: {
                public: ''
            },
        }
    }
});

function App() {
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState<any>([]);
    const [isUserLoggedIn, setUserLoggedIn] = useState("initializing");

    const fetchUser = async () => {
        const { attributes } = await Auth.currentAuthenticatedUser();
        try {
            const fetchUserQuery = `query MyQuery { getUser(id: "${attributes.sub}") { active trial companies { items { company { active active_subscription aiCPV { items { id } } cpv { items { id } } } } } } }`;
            const userData: any = await API.graphql(
                graphqlOperation(fetchUserQuery)
            );
            const userD = userData.data.getUser;
            setUserData(userD);
            setLoading(false);
        } catch (err) {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUser();
        checkAuthState();
    }, []);

    // Callback for when keywords are submitted from NoCpvsPage
    const handleKeywordsSubmitted = async () => {
        setLoading(true); // Optionally show a loading state
        await fetchUser(); // Re-fetch user data, which should now include CPVs/keywords
        // setLoading(false); // setLoading is handled within fetchUser or display logic
    };

    async function checkAuthState() {
        try {
            const user = await Auth.currentAuthenticatedUser();
            setUserLoggedIn("loggedIn");

            if (window.gtag){
                window.gtag('set', 'user_properties', {
                    user_type: user.attributes['custom:subscription_status'] || 'free_trial',
                    user_id: user.attributes.sub
                });
            }
            sendGAEvent('user_login');
        } catch (err) {
            setUserLoggedIn("loggedOut");
            setLoading(false);
        }
    }

    function display(loading: boolean, userData: any) {
        console.log('[App.tsx] display function called. isUserLoggedIn:', isUserLoggedIn);

        let hasCpvs = false;
        if (userData && userData.companies && userData.companies.items && userData.companies.items.length > 0) {
            const company = userData.companies.items[0].company;
            if (company) {
                const aiCpvHasItems = company.aiCPV && company.aiCPV.items && company.aiCPV.items.length > 0;
                const cpvHasItems = company.cpv && company.cpv.items && company.cpv.items.length > 0;
                hasCpvs = aiCpvHasItems || cpvHasItems;
            }
        }

        return (
            <SubscriptionProvider>
                <ViewLimitProvider>
                    <>
                        <GoogleAnalytics />
                        <Toaster position="top-right" reverseOrder={false} />
                        {loading === false && isUserLoggedIn !== "initializing" ? (
                            <BrowserRouter>
                                {isUserLoggedIn === "loggedIn" && (
                                    <TrialExpirationChecker />
                                )}
                                <Routes>
                                    <Route
                                        path="/"
                                        element={
                                            isUserLoggedIn === "loggedIn" ? (
                                                <Layout
                                                    active={userData.active}
                                                    subscription={
                                                        userData.companies.items[0]
                                                            .company.active_subscription
                                                    }
                                                    onboarding={userData.trial}
                                                    cpvs={hasCpvs}
                                                    // cpvs={false}
                                                    onKeywordsSubmitted={handleKeywordsSubmitted} // Pass down the callback
                                                />
                                            ) : (
                                                <Navigate
                                                    to={`/login?from=${window.location.pathname}`}
                                                />
                                            )
                                        }
                                    >
                                        {routes}
                                    </Route>
                                    <Route
                                        path="/login/"
                                        element={
                                            isUserLoggedIn === "loggedIn" ? (
                                                <Navigate to="/" />
                                            ) : (
                                                <Login />
                                            )
                                        }
                                    />
                                    <Route
                                        path="/register"
                                        element={
                                            isUserLoggedIn === "loggedIn" ? (
                                                <Navigate to="/" />
                                            ) : (
                                                <Onboarding />
                                            )
                                        }
                                    />
                                    <Route
                                        path="/forgot_password"
                                        element={
                                            isUserLoggedIn === "loggedIn" ? (
                                                <Navigate to="/" />
                                            ) : (
                                                <ForgotPassword />
                                            )
                                        }
                                    />
                                    <Route
                                        path="/payment_success"
                                        element={<PaymentSuccess />}
                                    />
                                    <Route
                                        path="/payment_failed"
                                        element={<PaymentFailed />}
                                    />
                                </Routes>
                            </BrowserRouter>
                        ) : (
                            <div className="flex w-screen h-screen justify-center items-center">
                                <p className="lg:text-7xl text-4xl flex text-white font-bold drop-shadow-2xl animate-bounce contrast-200">
                                    TenderTracer
                                </p>
                            </div>
                        )}
                    </>
                </ViewLimitProvider>
            </SubscriptionProvider>
        );
    }

    return <>{display(loading, userData)}</>;
}

export default App;
